<script setup lang="ts">
import { ActionButton, Dialog, StatusTag } from '@taler/ui'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import type { CreateContactInput } from '../composables/use-contacts.ts'

const props = defineProps<{ isSubmitting: boolean; errorMessage: string | undefined }>()
const emit = defineEmits<{ create: [input: CreateContactInput] }>()

const { t } = useI18n()
const open = ref(false)
const name = ref('')
const notes = ref('')

const canSubmit = computed(() => name.value.trim().length > 0 && name.value.trim().length <= 200)

function reset(): void {
  name.value = ''
  notes.value = ''
}

function close(): void {
  reset()
  open.value = false
}

function submit(): void {
  if (!canSubmit.value) return
  const trimmedNotes = notes.value.trim()
  emit('create', {
    name: name.value.trim(),
    ...(trimmedNotes === '' ? {} : { notes: trimmedNotes }),
  })
}

// A submission that succeeded clears the form and closes it; the composable's mutation
// state is the only source of truth for "did it work", so this watches it rather than
// guessing from a local flag.
watch(
  () => props.isSubmitting,
  (submitting, wasSubmitting) => {
    if (wasSubmitting && !submitting && props.errorMessage === undefined) close()
  },
)
</script>

<template>
  <div>
    <ActionButton type="button" @click="open = true">
      {{ t('contacts.addContact') }}
    </ActionButton>

    <Dialog :open="open" :label="t('contacts.addContactTitle')" @close="close">
      <form class="flex flex-col gap-4 p-[var(--space-block)]" @submit.prevent="submit">
        <h2 class="text-[length:var(--text-step-4)] font-semibold text-[color:var(--color-text)]">
          {{ t('contacts.addContactTitle') }}
        </h2>

        <label class="flex flex-col gap-1">
          <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
            t('contacts.nameLabel')
          }}</span>
          <input
            v-model="name"
            type="text"
            maxlength="200"
            required
            autofocus
            :placeholder="t('contacts.namePlaceholder')"
            class="h-[var(--density-control)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[color:var(--color-text)]"
          />
        </label>

        <label class="flex flex-col gap-1">
          <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
            t('contacts.notesLabel')
          }}</span>
          <textarea
            v-model="notes"
            maxlength="1000"
            rows="3"
            :placeholder="t('contacts.notesPlaceholder')"
            class="rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] py-[var(--space-inline)] text-[color:var(--color-text)]"
          />
        </label>

        <StatusTag v-if="errorMessage" tone="danger">{{ errorMessage }}</StatusTag>

        <div class="flex gap-2">
          <ActionButton type="submit" :disabled="!canSubmit || isSubmitting">
            {{ isSubmitting ? t('contacts.saving') : t('contacts.save') }}
          </ActionButton>
          <button
            type="button"
            class="min-h-[var(--density-target-min)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] px-3 py-2 text-[color:var(--color-text)]"
            @click="close"
          >
            {{ t('contacts.cancel') }}
          </button>
        </div>
      </form>
    </Dialog>
  </div>
</template>
